<template>
  <div>
    <div class="thumbnail">
      <div class="edit">
        <div>
          <button
            class="btn btn-primary btn-block"
            id="use-btn"
            :disabled="isFromAnotherUserDb"
            @click="selectTemplate(template)"
          >
            {{ $t('template.use') }}
          </button>
          <button
            class="btn btn-default btn-block"
            id="rename-btn"
            :disabled="readOnly === true || isFromAnotherUserDb"
            @click="renameTemplate(template)"
          >
            {{ $t('template.rename') }}
          </button>
          <button
            class="btn btn-default btn-block"
            :disabled="readOnly === true || isFromAnotherUserDb"
            id="duplicate-btn"
            @click="duplicateTemplate(template)"
          >
            {{ $t('template.copy') }}
          </button>
          <button
            class="btn btn-default btn-block"
            id="merge-tags-btn"
            v-if="isFirstLibraryActive"
            :disabled="isFromAnotherUserDb"
            @click="mergeTags(template)"
          >
            User Db
          </button>
          <button
            class="btn btn-default btn-block"
            v-if="isFirstLibraryActive"
            :disabled="readOnly === true || isFromAnotherUserDb"
            id="share-btn"
            @click="toggleShareTemplate(template)"
          >
            {{
              template.shared ? $t('template.unshare') : $t('template.share')
            }}
          </button>
          <button
            class="btn btn-default btn-block"
            id="download-btn"
            @click="saveTemplate(template)"
          >
            {{ $t('template.download') }}
          </button>
          <button
            class="btn btn-danger btn-block"
            id="delete-btn"
            :disabled="readOnly === true || isFromAnotherUserDb"
            @click="deleteTemplate(template)"
          >
            {{ $t('template.delete') }}
          </button>
          <button
            v-if="isCustomerCare"
            class="btn btn-default btn-block"
            id="json-btn"
            @click="downloadJsonDebaunced(template)"
          >
            JSON
          </button>
        </div>
        <div>
          <button
            class="btn btn-default btn-block"
            id="manage-tags-btn"
            v-if="isFirstLibraryActive"
            :disabled="readOnly === true || isFromAnotherUserDb"
            @click="manageTags(template)"
          >
            {{ $t('tags.button_open_modal') }}
          </button>
        </div>
      </div>
      <div class="template">
        <Thumbnail :url="template.thumbnail" :version="template.version" />
        <div class="caption">
          <div>
            <h3>
              {{ template.name }}
            </h3>
            <div v-if="isFirstLibraryActive || isFourthLibraryActive">
              <p class="author">
                {{ template.author }}
                <span v-if="isFourthLibraryActive"
                  >({{ template.ownerName }})</span
                >
              </p>
              <p v-if="template.mergeTagsSource" class="source-name">
                <font-awesome-icon icon="database" /> {{ sourceName }}
              </p>
              <p class="date">
                {{ formattedDate }}
                <span
                  v-if="template.shared && !isFourthLibraryActive"
                  class="label label-success"
                  >{{ $t('template.share_label') }}</span
                >
              </p>
              <div v-if="isFirstLibraryActive" class="clab-tag-container">
                <span
                  class="label label-primary"
                  v-for="tag in template.tags"
                  :key="tag.id"
                  >{{ tag }}</span
                >
              </div>
            </div>
            <div v-else>
              <br />
              <br />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {mapActions, mapGetters, mapState} from 'vuex';
import {debounce} from 'lodash';
import Thumbnail from './Thumbnail.vue';
import dateFormatter from '../functions/dateFormatter.js';

const SOURCE_NAME_PREFIX_LENGTH = 13;
const SOURCE_NAME_SUFFIX_LENGTH = 6;
const MAX_SOURCE_NAME_LENGTH =
  SOURCE_NAME_PREFIX_LENGTH + SOURCE_NAME_SUFFIX_LENGTH;

export default {
  name: 'Template',
  components: {
    Thumbnail
  },
  props: {
    template: Object,
    readOnly: Boolean,
    shared: Boolean
  },
  computed: {
    formattedDate: function () {
      return dateFormatter(this.template.lastModified);
    },
    ...mapState({
      userDb: state => state.userDb,
      templateName: state => state.templateName,
      dirtyTemplate: state => state.dirtyTemplate,
      isCustomerCare: state => state.isCustomerCare
    }),
    sourceName: function () {
      if (!this.template.mergeTagsSource) {
        return undefined;
      }

      const name = this.template.mergeTagsSource.name || '';

      if (name.length > MAX_SOURCE_NAME_LENGTH) {
        const name = this.template.mergeTagsSource.name;
        const length = this.template.mergeTagsSource.name.length;
        return (
          name.slice(0, SOURCE_NAME_PREFIX_LENGTH) +
          '...' +
          name.slice(length - SOURCE_NAME_SUFFIX_LENGTH, length)
        );
      }

      return name;
    },
    ...mapGetters([
      'showBackToWork',
      'isFirstLibraryActive',
      'isFourthLibraryActive'
    ]),
    isFromAnotherUserDb: function () {
      if (
        this.userDb &&
        this.template.mergeTagsSource &&
        this.template.mergeTagsSource.type === 'SEND'
      ) {
        return this.userDb !== this.template.mergeTagsSource.extended.userDbId;
      }
      return false;
    }
  },
  methods: {
    ...mapActions([
      'retrieveTemplate',
      'downloadTemplate',
      'downloadTemplateJson'
    ])
  },
  created: function () {
    this.saveTemplate = debounce(function (template) {
      const unsavedWork =
        this.dirtyTemplate !== undefined && this.dirtyTemplate !== '';
      const isTheSameTemplate =
        this.templateName !== undefined && this.templateName === template.name;
      if (unsavedWork && isTheSameTemplate) {
        this.$emit('download-unsaved-work', template);
      } else {
        this.downloadTemplate(template);
      }
    }, 500);
    this.downloadJsonDebaunced = debounce(function (template) {
      this.downloadTemplateJson(template);
    }, 500);
    this.selectTemplate = debounce(function (template) {
      if (this.showBackToWork) {
        this.$emit('unsaved-work', template);
      } else {
        this.retrieveTemplate(template);
      }
    }, 500);
    this.renameTemplate = debounce(function (template) {
      this.$emit('rename-template', template);
    }, 500);
    this.deleteTemplate = debounce(function (template) {
      this.$emit('delete-template', template);
    }, 500);
    this.duplicateTemplate = debounce(function (template) {
      this.$emit('duplicate-template', template);
    }, 500);
    this.toggleShareTemplate = debounce(function (template) {
      this.$emit('share-template', template);
    }, 500);
    this.mergeTags = debounce(function (template) {
      this.$emit('merge-tags', template);
    }, 500);
    this.manageTags = debounce(function (templateId) {
      this.$emit('template-tags', templateId);
    }, 500);
  }
};
</script>

<style scoped>
.edit {
  position: absolute;
  left: 50%;
  top: 5vh;
  transform: translate(-50%, 0);
  z-index: 50;
  opacity: 0;
  transition: opacity 0.5s ease;
}

.thumbnail {
  position: relative;
  background-color: white;
  transition: background-color 0.5s ease;
}

.thumbnail:hover {
  background-color: rgba(0, 0, 0, 0.3);
}

.thumbnail:hover .edit {
  opacity: 100;
}

.template {
  transition: opacity 0.5s ease;
}

.thumbnail .glyphicon.glyphicon-eye-open {
  color: #337ab7;
  font-size: 0.9em;
}

.thumbnail:hover > .template {
  opacity: 0.4;
}

.clab-tag-container span {
  margin-right: 8px;
  font-size: 1em;
  padding: 8px;
  border-radius: 24px;
}
.clab-tag-container span:last-child {
  margin-right: 0px;
}

.edit {
  top: 0px;
  bottom: 0px;
  display: flex;
  flex-direction: column;
}
.edit > div {
  padding-top: 26px;
  flex: 1;
}
.edit > div:last-of-type {
  padding-bottom: 18px;
  flex: 0;
}
.edit .btn {
  margin-bottom: 10px;
}
.edit .btn:last-of-type {
  margin-bottom: 0px;
}
</style>
